import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { getToken, getRefreshToken, setToken } from '@/utils/auth'
import router from '@/router'

// 用于刷新token的独立axios实例（不经过拦截器，避免循环）
function doRefreshToken(refreshToken) {
  const baseURL = import.meta.env.VITE_API_BASE_URL
  return axios.create({ baseURL, timeout: 10000 }).post('/auth/refresh', { refreshToken })
}

// 创建axios实例
const service = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL, // api的base_url
  timeout: 10000 // 请求超时时间
})

// Token刷新状态控制
let isRefreshing = false
let pendingRequests = []

// 执行排队中的请求
function onRefreshed(newToken) {
  pendingRequests.forEach(cb => cb(newToken))
  pendingRequests = []
}

// 将请求加入等待队列
function addPendingRequest(cb) {
  pendingRequests.push(cb)
}

// request拦截器
service.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    const token = getToken()

    if (token) {
      // 让每个请求携带token-- ['X-Token']为自定义key 请根据实际情况自行修改
      config.headers['Authorization'] = 'Bearer ' + token
    }

    // 添加时间戳防止缓存
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }

    return config
  },
  error => {
    // 对请求错误做些什么
    console.log(error) // for debug
    return Promise.reject(error)
  }
)

// 消息去重：防止同一消息短时间内重复弹出
let lastMessageInfo = { text: '', time: 0 }
function showMessage(options) {
  const now = Date.now()
  const text = options.message || ''
  // 同样的消息2秒内不重复弹出
  if (text === lastMessageInfo.text && now - lastMessageInfo.time < 2000) {
    return
  }
  lastMessageInfo = { text, time: now }
  ElMessage({
    ...options,
    grouping: true,  // Element Plus 内置去重
    duration: options.duration || 3000,
    zIndex: 10000    // 确保在所有弹窗遮罩之上（overlay=9000）
  })
}

// response 拦截器
service.interceptors.response.use(
  response => {
    // 如果是blob类型响应（文件下载），直接返回
    if (response.config.responseType === 'blob') {
      return response.data
    }

    const res = response.data

    // 如果自定义code不是200，则判断为错误
    if (res.code !== 200) {
      // 401 登录过期 → 触发重新登录
      if (res.code === 401) {
        handleLogout()
      } else if (res.code === 403) {
        // 403 权限不足 → 仅提示，不登出
        showMessage({
          message: res.message || '权限不足，拒绝访问',
          type: 'warning'
        })
      } else if (!response.config?.silentError) {
        showMessage({
          message: res.message || '操作未成功，请稍后重试',
          type: 'error'
        })
      }

      return Promise.reject(new Error(res.message || '操作未成功，请稍后重试'))
    } else {
      return res
    }
  },
  error => {
    const originalRequest = error.config

    // 处理HTTP 401：尝试用refreshToken自动刷新
    if (error.response && error.response.status === 401 && !originalRequest._isRetry) {
      const rtToken = getRefreshToken()

      if (rtToken) {
        if (isRefreshing) {
          // 已有刷新请求在进行中，将当前请求排队等待
          return new Promise(resolve => {
            addPendingRequest(newToken => {
              originalRequest.headers['Authorization'] = 'Bearer ' + newToken
              resolve(service(originalRequest))
            })
          })
        }

        isRefreshing = true
        originalRequest._isRetry = true

        return doRefreshToken(rtToken)
          .then(response => {
            const data = response.data?.data || response.data
            const newAccessToken = data.accessToken

            if (newAccessToken) {
              setToken(newAccessToken)
              const userStore = useUserStore()
              userStore.token = newAccessToken
              originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken
              onRefreshed(newAccessToken)
              return service(originalRequest)
            } else {
              handleLogout()
              return Promise.reject(error)
            }
          })
          .catch(() => {
            handleLogout()
            return Promise.reject(error)
          })
          .finally(() => {
            isRefreshing = false
          })
      } else {
        handleLogout()
        return Promise.reject(error)
      }
    }

    // 401 已在上面处理，不再重复弹消息
    if (error.response && error.response.status === 401) {
      return Promise.reject(error)
    }

    // 部分接口的参数校验错误已在表单层提示，无需再弹通用 toast 造成"双消息"
    // 命中条件：HTTP 400 + 来自 /auth/* 这类有完整前端校验的接口
    const url = (originalRequest && originalRequest.url) || ''
    const isAuthEndpoint = /\/auth\//.test(url)
    if (error.response && error.response.status === 400 && isAuthEndpoint) {
      return Promise.reject(error)
    }

    let message = error.message
    if (error.response) {
      switch (error.response.status) {
        case 400:
          message = '输入有误，请检查后重试'
          break
        case 403:
          message = '没有访问权限'
          break
        case 404:
          message = '请求的资源不存在'
          break
        case 408:
          message = '请求超时，请稍后重试'
          break
        case 429:
          // 429 由后端业务码场景使用（如登录限流），通常会带具体 message
          message = error.response.data?.message || '操作过于频繁，请稍后再试'
          break
        case 500:
          message = '服务暂时不可用，请稍后重试'
          break
        case 502:
        case 503:
        case 504:
          message = '服务连接异常，请稍后重试'
          break
        default:
          message = '请求异常，请稍后重试'
      }
    } else if (error.code === 'ECONNABORTED') {
      message = '请求超时，请稍后重试'
    } else if (error.message && error.message.includes('Network Error')) {
      message = '网络连接异常，请检查网络'
    }

    showMessage({
      message: message,
      type: 'error'
    })

    return Promise.reject(error)
  }
)

// 统一处理登出跳转
let isLoggingOut = false
function handleLogout() {
  if (isLoggingOut) return
  isLoggingOut = true

  const userStore = useUserStore()
  userStore.resetToken()

  ElMessageBox.confirm(
    '登录状态已过期，请重新登录',
    '系统提示',
    {
      confirmButtonText: '重新登录',
      showCancelButton: false,
      type: 'warning'
    }
  ).then(() => {
    router.push('/login')
  }).finally(() => {
    isLoggingOut = false
  })
}

export default service
